import React, { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import PageHeader from '../../Common/PageHeader';
import Icons from '../../../../components/Common/Icons/Icons';
import ContentCard from '../../Common/Card';
import Footer from '../../Layout/Footer';
import FakePagination from '../../../../components/Common/UI/FakePagination';
import DataTable from '../../../../components/Common/UI/DataTable';
import {
  batchDocumentsCol,
  batchDocumentsData,
  EnumBatchDocumentsStatus,
  IBatchDocumentsData,
  ISearchResultData,
} from './Data';
import DocumentsSearch from './Search';

export const searchArticlesData: ISearchResultData[] = Array.from({ length: 550 }, (_, i) => {
  const dateBase = dayjs().subtract(i, 'day');

  return {
    id: (i + 1).toString(),
    account: `****${(1000 + (i % 9000)).toString()}`,
    checked: false,
    date: dateBase.format('MMM DD, YYYY'),
    documentType: ['Tax', 'Statement', 'Notice'][i % 3],
    pageCount: ((i % 10) + 1).toString(),
    from: dateBase.subtract(60, 'day').format('MMM DD, YYYY'),
    to: dateBase.format('MMM DD, YYYY'),
  };
});

function BatchDownload() {
  const topRef = useRef<HTMLDivElement>(null);

  const [searchResult, setSearchResult] = useState<ISearchResultData[]>([]);
  const [data, setData] = useState<IBatchDocumentsData[]>([]);

  const getSelectedAccounts = (): string[] => {
    const viewerUpdatesRaw = localStorage.getItem('viewerUpdates');

    if (!viewerUpdatesRaw) return [];

    try {
      const viewerUpdates = JSON.parse(viewerUpdatesRaw);

      return viewerUpdates?.[0]?.selectedAccounts || [];
    } catch {
      return [];
    }
  };

  const getBatchData = (): IBatchDocumentsData[] => {
    const selectedAccounts = getSelectedAccounts();
    let baseData = [...batchDocumentsData];

    if (selectedAccounts.length === 1) {
      baseData = baseData.map(doc => ({
        ...doc,
        accounts: selectedAccounts[0],
      }));
    }

    return baseData;
  };

  useEffect(() => {
    setData(getBatchData());
  }, []);

  useEffect(() => {
    if (!searchResult.length) {
      setData(getBatchData());
    }
  }, [searchResult]);

  const handleSearchResult = (payload: ISearchResultData[]) => {
    setSearchResult(payload);
  };

  const handleCompressIntoPDF = () => {
    const checked = searchResult.filter(el => el.checked);

    if (!checked.length) return;

    const newDocument: IBatchDocumentsData = {
      id: (data.length + 1).toString(),
      submittedDate: `${dayjs().format('MMM DD, YYYY HH:mm')} PDT`,
      from: checked[0].from || 'MAR 20, 2024',
      to: checked[0].to || 'MAR 20, 2024',
      accounts: checked[0].account,
      documentsType: checked[0].documentType,
      status: EnumBatchDocumentsStatus.pending,
      type: 'COMBINED',
      expiration: `${dayjs().add(1, 'month').format('MMM DD, YYYY HH:mm')} PDT`,
    };

    setData(prev => [...prev, newDocument]);

    if (topRef.current) {
      topRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="grid grid-rows-[auto,1fr]" ref={topRef}>
      <PageHeader icon={<Icons.Account />} title="Batch Download" ignoreAccount />

      <div className="px-[20px] grid gap-[20px] grid-rows-[auto,1fr,auto]">
        <ContentCard title="Batch Download Tasks">
          <DataTable
            responsive
            minWidth={1366}
            variant="guidance2"
            columns={batchDocumentsCol}
            data={data}
            paginationPosition="bottom"
            paginationVariant="guidance"
            initialState={{ pagination: { pageSize: 10 } }}
          />

          <div className="mt-[30px] flex justify-end">
            <FakePagination value="1-5 of 5" />
          </div>
        </ContentCard>

        <DocumentsSearch
          handleSearchResult={handleSearchResult}
          searchResult={searchResult}
          handleCompressIntoPDF={handleCompressIntoPDF}
        />

        <Footer className="self-end" />
      </div>
    </div>
  );
}

export default BatchDownload;
